import { dialog } from "../../../../../nq-component";
import BaseFormPresenter from "../../../../../base/BaseFormPresenter";
import DialogModal from "../../../../../components/Dialog";
import { findObjectUseCase } from "../../../../../usecases/object";
import { auditLogUseCase } from "../../../../../usecases/logs";
import getChangedFields from "../../../../../getChangedFields";

class RawMaterialFormPresenter extends BaseFormPresenter {
  computePartialCost(usedQty, stockQty, stockCost) {
    if (
      !isNaN(usedQty) &&
      !isNaN(stockQty) &&
      stockQty > 0 &&
      !isNaN(stockCost)
    ) {
      return ((usedQty / stockQty) * stockCost).toFixed(2);
    }
    return "0.00";
  }

  async updateComponentRawMaterialUsage(rawMaterialId) {
    const newCost = this.change.cost ?? this.object.cost;
    const newStockQty =
      this.change.stock_quantity ?? this.object.stock_quantity;

    const updatedProductIds = new Set();

    // ----------------------------
    // PHASE 1: Components -> Products via components
    // ----------------------------
    const affectedComponents = await findObjectUseCase().execute("components", {
      where: { "raw_materials.id": rawMaterialId },
    });

    for (const component of affectedComponents) {
      const updatedRawMaterials = (component.raw_materials || []).map((raw) => {
        const isMatched = raw.id === rawMaterialId;
        const cost = isMatched ? newCost : raw.cost;
        const stockQty = isMatched ? newStockQty : raw.stock_quantity;

        const partial_cost = this.computePartialCost(
          parseFloat(raw.quantity || "0"),
          parseFloat(stockQty || "0"),
          parseFloat(cost || "0"),
        );

        return { ...raw, cost, stock_quantity: stockQty, partial_cost };
      });

      const totalComponentCost = updatedRawMaterials
        .reduce((acc, raw) => acc + parseFloat(raw.partial_cost || "0"), 0)
        .toFixed(2);

      const updatedComponent = {
        ...component,
        raw_materials: updatedRawMaterials,
        cost: totalComponentCost,
      };

      await this.upsertUseCase.execute("components", updatedComponent);

      const affectedProducts = await findObjectUseCase().execute("products", {
        where: { "components.id": component.id },
      });

      for (const product of affectedProducts) {
        updatedProductIds.add(product.id);

        const updatedProductComponents = (product.components || []).map(
          (comp) => (comp.id === component.id ? updatedComponent : comp),
        );

        const updatedProductRawMaterials = (product.raw_materials || []).map(
          (raw) => {
            const isMatched = raw.id === rawMaterialId;
            const cost = isMatched ? newCost : raw.cost;
            const stockQty = isMatched ? newStockQty : raw.stock_quantity;

            const partial_cost = this.computePartialCost(
              parseFloat(raw.quantity || "0"),
              parseFloat(stockQty || "0"),
              parseFloat(cost || "0"),
            );

            return { ...raw, cost, stock_quantity: stockQty, partial_cost };
          },
        );

        const rawCost = updatedProductRawMaterials.reduce(
          (sum, raw) => sum + parseFloat(raw.partial_cost || "0"),
          0,
        );

        const totalProductCost = rawCost.toFixed(2);

        const updatedProduct = {
          ...product,
          raw_materials: updatedProductRawMaterials,
          components: updatedProductComponents,
          cost: totalProductCost,
        };

        await this.upsertUseCase.execute("products", updatedProduct);
      }
    }

    // ----------------------------
    // PHASE 2: Direct Raw Material -> Products (even if no components)
    // ----------------------------

    const directProducts = await findObjectUseCase().execute("products", {
      where: {
        "others_raw_materials.id": rawMaterialId,
      },
    });

    for (const product of directProducts) {
      const updatedProductRawMaterials = (
        product.others_raw_materials || []
      ).map((raw) => {
        const isMatched = raw.id === rawMaterialId;
        const cost = isMatched ? newCost : raw.cost;
        const stockQty = isMatched ? newStockQty : raw.stock_quantity;

        const partial_cost = this.computePartialCost(
          parseFloat(raw.quantity || "0"),
          parseFloat(stockQty || "0"),
          parseFloat(cost || "0"),
        );

        return { ...raw, cost, stock_quantity: stockQty, partial_cost };
      });

      const rawCost = updatedProductRawMaterials.reduce(
        (sum, raw) => sum + parseFloat(raw.partial_cost || "0"),
        0,
      );

      const updatedProduct = {
        ...product,
        raw_materials: updatedProductRawMaterials,
        cost: rawCost.toFixed(2),
      };

      await this.upsertUseCase.execute("products", updatedProduct);
    }
  }

  async save() {
    const collection = this.view.getCollectionName();
    const user = this.view.getCurrentUser();
    const fullAfter = { ...this.object, ...this.change };
    const changedFields = getChangedFields(this.object || {}, fullAfter);

    const isNew = !this.object?.id;
    const changedKeys = Object.keys(this.change).filter((k) => k !== "id");
    const summary = changedKeys
      .map((key) => `${key}: ${this.change[key]}`)
      .join(", ");

    const message = isNew
      ? `Created new Raw Material: ${this.change.itemName}`
      : changedFields.length === 0
        ? `Updated Raw Material: ${this.object.itemName} — changed: ${summary}`
        : `Updated Raw Material: ${this.object.itemName} — changed: ${summary}`;

    if (this.object.id) {
      this.change.id = this.object?.id;
    } else {
      this.change.acl = this.view.getAcl();
    }

    if (this.change?.category?.id) {
      this.change.category = { id: this.change.category.id };
    } else {
      delete this.change.category;
    }

    try {
      const result = await this.upsertUseCase.execute(collection, this.change);

      await auditLogUseCase()[isNew ? "create" : "update"]({
        user,
        logDate: new Date().toISOString(),
        message,
        collection,
        objectId: result?.id,
        payload: result,
        // store: { id: result.storeId?.id },
      });

      if (this.change.id) {
        await this.updateComponentRawMaterialUsage(this.change.id);
      }

      this.change = {};
    } catch (error) {
      throw error;
    }
  }

  async submit() {
    if (Object.values(this.change).length === 0) {
      this.view.showSuccessSnackbar("Successfully saved!");
      return;
    }
    try {
      this.view.submitting();
      dialog.close();
      await this.save();

      dialog.fire({
        html: (
          <DialogModal
            icon="bi bi-question-octagon-fill"
            iconColor="#82c14a"
            title="Saved Successfully"
            message=""
            isCancelButton={false}
            onSubmit={() => {
              this.view.navigateBack();
              dialog.close();
            }}
            onClose={() => dialog.close()}
          />
        ),
        footer: false,
      });
    } catch (error) {
      this.view.submissionError(error);
      this.view.showError(error);
    }
  }
}

export default RawMaterialFormPresenter;
